import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PlayerSearchSelector from '../../components/PlayerSearchSelector';
import ErrorAlert from '../../components/ErrorAlert';
import { useAdminData } from '../../../contexts/AdminDataContext';
import { apiGet, apiPost, apiPut } from '../../../services/apiFetch';
import '../../../styles/TeamForm.css';

const TeamForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const { allPlayers, refreshTeams } = useAdminData();

  const [formData, setFormData] = useState({ name: '', image: '', website: '', country: '', player_ids: [] });
  const [teamPlayers, setTeamPlayers] = useState([]);           // selected
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // compute only the players _not_ yet on the team
  const availablePlayers = useMemo(
    () => allPlayers.filter(
      p => !teamPlayers.some(tp => tp.id === p.id)
    ),
    [allPlayers, teamPlayers]
  );

  const handlePlayerSelect = player => {
    setTeamPlayers(prev => [...prev, player]);
  };

  const handleRemovePlayer = playerId => {
    setTeamPlayers(prev =>
      prev.filter(p => p.id !== playerId)
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // If editing, fetch the team itself + its current players
        if (isEditing) {
          const { data: teamJson, error: teamErr } = await apiGet(`/api/rl/teams/${id}`);
          if (teamErr) throw new Error(teamErr.error);
          // Ensure all form fields are strings, not null
          setFormData({
            name: teamJson.team.name || '',
            image: teamJson.team.image || '',
            website: teamJson.team.website || '',
            country: teamJson.team.country || '',
            player_ids: teamJson.team.player_ids || []
          });

          const ids = teamJson.team.player_ids;
          const { data: teamPlayersJson, error: playersErr } = await apiGet(`/api/rl/teams/${id}/players?image=true&player_ids=${ids}`);
          if (playersErr) throw new Error(playersErr.error);
          setTeamPlayers(teamPlayersJson.players);
        }
      }
      catch (err) {
        console.error(err);
      }
      finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = (e) => {
    // In a real application, this would handle file uploads
    // For this demo, we'll just set the image URL directly
    const file = e.target.files[0];
    if (file) {
      // Mock image URL
      const imageUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        image: imageUrl
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setApiError(null);

    try {
      if (isEditing) {
        const { data, error } = await apiPut(`/api/rl/teams/${id}`, { ...formData, player_ids: teamPlayers.map(p => p.id) });
        if (error) {
          setApiError(error);
          return;
        }
        console.log(data)
      } else {
        const { data, error } = await apiPost('/api/rl/teams', { ...formData, player_ids: teamPlayers.map(p => p.id) });
        if (error) {
          setApiError(error);
          return;
        }
        console.log(data)
      }

      await refreshTeams();
      navigate('/admin/teams');
    } catch (error) {
      console.error("Error submitting team:", error);
      setApiError({
        error: 'internal_error',
        message: error.message || 'Error submitting team. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="team-form-page">
      <div className="page-header">
        <button type="button" className="back-button" onClick={() => navigate(-1)} style={{marginLeft: 16}}>
          ← Back
        </button>
        <h1>{isEditing ? 'Edit Team' : 'Create Team'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-section">
          <h2>Team Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label>Team Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Team Logo</label>

              {formData.image && (
                <div className="image-preview">
                  <img
                    src={formData.image}
                    alt={formData.name}
                    onError={(e) => { e.target.src = '/assets/images/placeholder.png' }}
                  />
                </div>
              )}

              <div className="file-input">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <div className="file-input-info">
                  Supported formats: JPEG, PNG. Max size: 2MB
                </div>
              </div>

              {/* For demo purposes, allow direct URL input */}
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="Or enter image URL directly"
                className="mt-2"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Team Players</label>
              <PlayerSearchSelector
                items={availablePlayers}
                onSelect={handlePlayerSelect}
                displayProperty='username'
                idProperty='id'
                placeholder='Search for existing player...'
              />
            </div>
          </div>

          {teamPlayers.length > 0 && (
            <div className="form-row">
              <div className="form-group">
                <label>Selected Players</label>
                <div className="team-players">
                  {teamPlayers.map(player => (
                    <div key={player.id} className="team-player">
                      <img
                        src={player.image}
                        alt={player.username}
                        className="player-avatar"
                        onError={(e) => { e.target.src = '/assets/images/placeholder.png' }}
                      />
                      <span className="player-name">{player.username}</span>
                      <button
                        type="button"
                        className="remove-player-btn"
                        onClick={() => handleRemovePlayer(player.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>




        {/*{isEditing && (
          <div className="form-section">
            <h2>Team Players</h2>

            {players.length === 0 ? (
              <div className="no-players">No players on this team</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Username</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(player => (
                    <tr key={player.id}>
                      <td>
                        <div className="player-info">
                          <img
                            src={player.image}
                            alt={player.username}
                            className="player-avatar"
                            onError={(e) => { e.target.src = '/assets/images/placeholder.png' }}
                          />
                          <span>{player.username}</span>
                        </div>
                      </td>
                      <td>{player.username}</td>
                      <td>
                        <Link to={`/admin/players/edit/${player.id}`} className="create-button" state={{ from: 'team-form' }}>
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}*/}

        <ErrorAlert 
          error={apiError} 
          onDismiss={() => setApiError(null)}
          title="Error Submitting Team"
        />

        <div className="form-actions">
          <button
            type="button"
            className="cancel-button"
            onClick={handleCancel}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (isEditing ? 'Update Team' : 'Create Team')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamForm;
